/**
 * @file MissingFormulationsModal.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-10-08
 * @description The products in a Map of Ag report that have no formulation,
 *              and the three things that can be done about each: add one,
 *              borrow another product's, or leave it out of this report.
 *
 *              A modal rather than a panel above the contact table. The panel
 *              pushed the table someone was working in off the screen, and it
 *              was the wrong shape for a list with per-row decisions in it.
 *
 *              EXCLUDING IS FOR THIS REPORT ONLY and is not stored. Leaving a
 *              product out of one processor's return says nothing about the
 *              product, so remembering it would be remembering the wrong thing
 *              — the next report starts from everything again.
 */

import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import { useSlideOutButton } from './SlideOutButton';
import './TableStyles.css';
import './MissingFormulationsModal.css';
import { formatQuantity } from '../utils/numberFormatters';

// A tonnage, by the one quantity rule (the column heading carries the T).
const t4 = (n) => formatQuantity(n) || '—';

function MissingFormulationsModal({
  isOpen, missing = [], borrowed = [], excluded = [],
  onExcludeChange, onAddFormulation, onBorrow, onClose
}) {
  useSlideOutButton([isOpen, missing.length, excluded.length]);
  if (!isOpen) return null;

  const isExcluded = (code) => excluded.includes(code);
  const toggle = (code, on) => onExcludeChange(
    on ? excluded.filter((c) => c !== code) : [...excluded, code]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Products with no formulation"
      className="missing-formulations"
      size="xl"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">Done</span>
            </button>
          </div>
        </div>
      }
    >
      <p className="mfm-explainer">
        These products were delivered in the period but have no formulation, so their soya and
        protein columns would be blank — which a processor will read as zero. Add a formulation,
        borrow another product's, or leave the product out of this report. <strong>Leaving one out
        applies to this report only</strong> and is not remembered.
      </p>

      <div className="table-container mfm-list">
        <table className="data-table">
          <thead>
            <tr>
              <th className="checkbox-col no-sort" title="Untick to leave this product out of the report">In</th>
              <th>Product</th>
              <th className="num">Tonnes</th>
              <th className="num">Accounts</th>
              <th className="mfm-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {missing.map((m) => {
              // `excluded` comes back from the server so a product left out
              // keeps its place in the list, struck through, instead of
              // vanishing and leaving the decision invisible.
              const out = isExcluded(m.item_code) || m.excluded;
              return (
                <tr key={m.item_code} className={`line-row${out ? ' is-excluded' : ''}`}>
                  <td className="checkbox-col">
                    <TickCheckbox
                      id={`mfm-${m.item_code}`}
                      checked={!out}
                      onChange={(e) => toggle(m.item_code, e.target.checked)}
                      ariaLabel={`Include ${m.item_code} in this report`}
                    />
                  </td>
                  <td>
                    <span className="account-badge-inline">{m.item_code}</span>
                    <span className="mfm-name">{m.item_name}</span>
                  </td>
                  <td className="num">{t4(m.tonnes)}</td>
                  <td className="num">{m.accounts?.length ?? '—'}</td>
                  <td>
                    {/* Both actions always render; the one that cannot apply is
                        disabled with a reason rather than dropped. */}
                    <div className="actions-cell">
                      <button
                        className={`btn-icon${out ? ' btn-disabled' : ''}`}
                        onClick={() => onAddFormulation(m)}
                        disabled={out || !m.inventory_item_id}
                        title={out ? 'Left out of this report'
                          : !m.inventory_item_id ? 'This product is not in the inventory'
                          : 'Add a formulation for this product'}
                      >
                        <span className="material-symbols-rounded">add_chart</span>
                      </button>
                      <button
                        className={`btn-icon${out ? ' btn-disabled' : ''}`}
                        onClick={() => onBorrow(m)}
                        disabled={out || !m.inventory_item_id}
                        title={out ? 'Left out of this report'
                          : !m.inventory_item_id ? 'This product is not in the inventory'
                          : "Use another product's formulation"}
                      >
                        <span className="material-symbols-rounded">swap_horiz</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!missing.length && (
              <tr><td colSpan={5} className="report-empty">Every product in this report has a formulation.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {borrowed.length > 0 && (
        <div className="mfm-borrowed">
          <h4>Already borrowing another product's analysis</h4>
          <ul>
            {borrowed.map((b) => (
              <li key={b.item_code}>
                <span className="account-badge-inline">{b.item_code}</span>
                <span className="mfm-name">{b.item_name}</span>
                <span className="material-symbols-rounded mfm-arrow">arrow_forward</span>
                <span className="account-badge-inline">{b.from_code}</span>
                <span className="mfm-name">{b.from_name}</span>
              </li>
            ))}
          </ul>
          <p className="mfm-borrowed-note">
            Managed in Settings. A formulation added to the product itself always wins.
          </p>
        </div>
      )}
    </Modal>
  );
}

export default MissingFormulationsModal;
